/**
 * Favotrip Exclusive voor ingelogde leden op favotrip.de (brief A3, Dennis
 * 04-11-2026: «Zet alles in t werk om 10 november live te kunnen gaan»).
 *
 * Alles hier staat achter LUXE_DE_PUBLIEK_LIVE (src/lib/luxe/luxe-de-flags.js,
 * standaard uit). Uit: geen enkele functie hieronder zegt ja, dus .de en .nl
 * zijn precies main.
 *
 * DE PRIJS. Op .de zit de € 19 reserveringsfee in de prijs per persoon
 * (Prijsbeleid «In de prijs», `pricing.fee_included_site_domains`,
 * src/lib/site-pricing-domain.js). Favotrip Exclusive rekent nergens
 * reserveringskosten, ook niet op .nl (fixronde 2 fe#1938, punt 2): de
 * zijbalk, het formulier en de mobiele balk zetten ze voor een Luxe-deal op 0
 * (SidePriceInfo.js:1304-1305 `reservationCost`, ReservationForm.js:1954-1955
 * en :2074), de reserveringsroute rekent er nooit zelf mee
 * (src/app/api/booking/reserve/route.js:150-153). Er is dus geen fee om in de
 * ledenprijs p.P. te verdelen (bewijs:
 * src/components/booking/exclusive-de-kassa-geen-fee.regression.test.jsx; zie
 * ook activeringspunt `reserveringskosten_alle_laadstanden` en
 * `LUXE_FEE_CHECKOUT_READY`, src/lib/luxe/luxe-booking-activation.test.js).
 * De Exclusive-klantprijs is dus «alles in de prijs»: per persoon een heel
 * bedrag op een 9, naar boven afgerond met één functie
 * (`exclusiveDeKlantprijsPp`, fixronde 5 fe#1938) op hotelpagina, kalender,
 * offerte en reservering, zonder losse feeregel. Daarom stuurt de frontend in Luxe-modus ook op .de
 * GEEN `domain` naar de backend: een .de-domein in Luxe-modus is daar het
 * ING-signaal (src/lib/luxe/ing-de-boeken-forward.js), en de websiteregel zou
 * er niets aan toevoegen. Hotelpagina, kalender, zijbalk en kassa tonen zo
 * dezelfde som: `exclusiveDeKassaSom`.
 *
 * Geen `server-only` en geen `next/*`: middleware, routes en de boekstraat in
 * de browser lezen dezelfde regel.
 */
import { isFavotripDeHost, isFavotripDeInBrowser } from "./ing-de";
import { luxeDePubliekLive } from "./luxe-de-flags";
import { isLuxeDeal } from "./luxe-deal";
import { formatEurDe } from "@/components/ing-de/ing-de-opmaak";

// Dezelfde ladder als snapToNineEnding (src/lib/currency-convert.js, gelijk aan
// de backend): het kleinste n*10 + 9 >= bedrag. Bewust hier en niet
// geïmporteerd: veel boekstraattests bootsen currency-convert na zonder deze
// export, en een getest gelijk (exclusive-de-leden.test.js) houdt beide gelijk.
function opEenNegenNaarBoven(bedrag) {
  return Math.ceil((bedrag - 9) / 10) * 10 + 9;
}

/** De Exclusive-pagina's voor leden op .de zijn altijd Duits. */
export const EXCLUSIVE_DE_LOCALE = "de";

/**
 * Het anker van het Duitse aanmeldblok op het poortscherm van .de
 * (src/components/luxe/ExclusiveDeAanmelden.jsx); de inlogknop
 * (luxeLoginTarget) wijst hierheen.
 */
export const EXCLUSIVE_DE_ANMELDEN_ANKER = "exclusive-de-anmelden";

/** Reserveringskosten van een Exclusive-boeking, ook op .de: geen (zie de kop). */
export const EXCLUSIVE_DE_RESERVERINGSKOSTEN_EUR = 0;

/**
 * Geldt de Exclusive-ledenomgeving op deze host of dit domein? Alleen op
 * favotrip.de (ook www. en staging.) en alleen met de schakelaar aan.
 */
export function exclusiveDeLedenAan(hostOrDomain) {
  return luxeDePubliekLive() && isFavotripDeHost(hostOrDomain);
}

/** Als `exclusiveDeLedenAan`, voor de huidige browserhost (op de server: false). */
export function exclusiveDeLedenAanInBrowser() {
  return luxeDePubliekLive() && isFavotripDeInBrowser();
}

/**
 * De taal voor de bedragen in de boekstraat: "de" voor een Exclusive-deal op
 * .de met de schakelaar aan («218 €», «Gesamt 218 €»), anders undefined, zodat
 * elke aanroep van formatPrice/formatPriceText letterlijk die van main blijft
 * («€ 218,-»).
 *
 * @param {object | null | undefined} deal
 * @param {{ domain?: string | null } | null | undefined} siteContext
 * @returns {"de" | undefined}
 */
export function exclusiveDeBedragLocale(deal, siteContext) {
  return isLuxeDeal(deal) && exclusiveDeLedenAan(siteContext?.domain) ? EXCLUSIVE_DE_LOCALE : undefined;
}

/**
 * Een ledenprijs per persoon (een heel bedrag op een 9, uit de backend) en
 * het gezelschap als de som die de kassa rekent: per persoon keer het aantal
 * personen, plus de reserveringskosten (0). Ongeldige invoer: null.
 *
 * @param {unknown} perPersoon
 * @param {unknown} gasten
 * @returns {null | { perPersoon: number, gasten: number, reserveringskosten: number, totaal: number }}
 */
export function exclusiveDeKassaSom(perPersoon, gasten) {
  const pp = typeof perPersoon === "string" && perPersoon.trim() !== "" ? Number(perPersoon) : perPersoon;
  if (typeof pp !== "number" || !Number.isFinite(pp) || pp <= 0) return null;
  if (!Number.isSafeInteger(gasten) || gasten < 1) return null;
  const totaal = Math.round((pp * gasten + EXCLUSIVE_DE_RESERVERINGSKOSTEN_EUR) * 100) / 100;
  return { perPersoon: pp, gasten, reserveringskosten: EXCLUSIVE_DE_RESERVERINGSKOSTEN_EUR, totaal };
}

/**
 * DE ene afrondfunctie van de Exclusive-klantprijs op .de (fixronde 5
 * fe#1938): per persoon naar boven op een bedrag dat op een 9 eindigt
 * (de ladder van snapToNineEnding; 243 -> 249, 249,50 -> 259, 249 blijft
 * 249), fee 0.
 * Hotelkaart en hub (exclusive-de-model.js), kalender (DateSelection.js),
 * offerte (/api/pricing/quote) en reservering (/api/booking/reserve) lezen
 * alle vier deze functie, zodat wat de klant ziet is wat hij betaalt.
 * Centen eerst naar boven op de cent; ruis van de zwevende komma (minder dan
 * een duizendste cent, 249.0000001) telt niet mee. Ongeldig: null.
 *
 * @param {unknown} eur  bronprijs per persoon
 * @returns {number | null}
 */
export function exclusiveDeKlantprijsPp(eur) {
  const n = typeof eur === "string" && eur.trim() !== "" ? Number(eur) : eur;
  if (typeof n !== "number" || !Number.isFinite(n) || n <= 0) return null;
  const exact = n * 100;
  const dichtst = Math.round(exact);
  const centen = dichtst < exact - 1e-3 ? dichtst + 1 : dichtst;
  return opEenNegenNaarBoven(centen / 100);
}

/**
 * Het klanttotaal van een Exclusive-boeking op .de uit een brontotaal voor
 * het hele gezelschap: per persoon via `exclusiveDeKlantprijsPp`, dan keer
 * het aantal personen (`exclusiveDeKassaSom`, fee 0). 486 voor 2 -> 498.
 * Ongeldige invoer: null.
 *
 * @param {unknown} totaal
 * @param {unknown} gasten
 * @returns {number | null}
 */
export function exclusiveDeKlanttotaal(totaal, gasten) {
  const n = typeof totaal === "string" && totaal.trim() !== "" ? Number(totaal) : totaal;
  if (typeof n !== "number" || !Number.isFinite(n) || n <= 0) return null;
  if (!Number.isSafeInteger(gasten) || gasten < 1) return null;
  return exclusiveDeKassaSom(exclusiveDeKlantprijsPp(n / gasten), gasten)?.totaal ?? null;
}

// Vanaf 18 jaar volwassen (buildPricingInput: een kind is 0 < leeftijd <= 17).
const VOLWASSEN_VANAF = 18;

const ISO_DATUM = /^(\d{4})-(\d{2})-(\d{2})/;

/** Is deze regel uit `pricing_input.guests` op de peildatum (checkin) een volwassene? Onbekend: null. */
function volwassenOp(gast, peildatum) {
  if (!gast || typeof gast !== "object") return null;
  if (typeof gast.age === "number" && Number.isFinite(gast.age)) return gast.age >= VOLWASSEN_VANAF;
  const dob = typeof gast.dateOfBirth === "string" ? ISO_DATUM.exec(gast.dateOfBirth) : null;
  const peil = typeof peildatum === "string" ? ISO_DATUM.exec(peildatum) : null;
  if (!dob || !peil) return null;
  const grens = `${String(Number(peil[1]) - VOLWASSEN_VANAF).padStart(4, "0")}-${peil[2]}-${peil[3]}`;
  return `${dob[1]}-${dob[2]}-${dob[3]}` <= grens;
}

function volwassenenVan(invoer) {
  if (!invoer || typeof invoer !== "object") return null;
  // pricing_input (buildPricingInput): één regel per persoon, kinderen met
  // hun geboortedatum; volwassen op de dag van `checkin`.
  if (Array.isArray(invoer.guests)) {
    let som = 0;
    for (const gast of invoer.guests) {
      const volwassen = volwassenOp(gast, invoer.checkin);
      if (volwassen === null) return null;
      if (volwassen) som += 1;
    }
    return som >= 1 ? som : null;
  }
  // booking_info_receiver.travel_rooms: per kamer de volwassenen.
  if (Array.isArray(invoer.travel_rooms)) {
    if (!invoer.travel_rooms.length) return null;
    let som = 0;
    for (const kamer of invoer.travel_rooms) {
      const n = volwassenenVan(kamer);
      if (n === null) return null;
      som += n;
    }
    return som;
  }
  const volwassenen = invoer.adults;
  if (!Number.isSafeInteger(volwassenen) || volwassenen < 1) return null;
  return volwassenen;
}

/**
 * DE personentelling van de Exclusive-klantprijs op .de (fixronde 6 fe#1938,
 * punt 3): het aantal VOLWASSENEN, dezelfde telling waarmee de prijsinvoer de
 * hotelprijs per persoon vermenigvuldigt (useBookingQuote.js `hotelPax` =
 * `hotelChargeablePax({ adults })`, src/lib/booking/voucher-stay-cost.js).
 * Een kind slaapt mee op de kamer en telt niet mee: 2 volwassenen + 1 kind met
 * kalender 249 p. P. is hotel 498, dus 249 p. P. en 498, niet 498 / 3 -> 169
 * p. P. / 507. De kalender (DateSelection), de offerte en de reservering
 * delen zo door hetzelfde getal.
 *
 * Eerste bruikbare bron: een prijsinvoer (`guests`-lijst met geboortedata en
 * `checkin`, of het getal `adults`) of een `booking_info_receiver`
 * (`travel_rooms`). Null als geen bron eenduidig is.
 *
 * @param {...(object | null | undefined)} bronnen
 * @returns {number | null}
 */
export function exclusiveDeGasten(...bronnen) {
  for (const bron of bronnen) {
    const n = volwassenenVan(bron);
    if (n !== null) return n;
  }
  return null;
}

/** «109 €» voor een heel bedrag, «109,50 €» met centen (de-DE, zoals de ING-pagina's). */
export function formatExclusiveDeEur(bedrag) {
  const n = typeof bedrag === "string" && bedrag.trim() !== "" ? Number(bedrag) : bedrag;
  if (typeof n !== "number" || !Number.isFinite(n)) return null;
  return formatEurDe(Math.round(n * 100) / 100);
}
